/**
 * HVP DOM Patcher
 *
 * Smart DOM patching system to prevent flickering and maintain performance.
 * Only adds/removes changed options instead of rebuilding entire dropdowns.
 *
 * Features:
 * - Smart patching algorithm (diff-based updates)
 * - Position-aware insertion (maintains option order)
 * - Value preservation (current selection survives updates)
 * - Label caching (avoids repeated string processing)
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { DomPatcherLabelMethods } from './dom-patcher-labels.js';

export class HVPDomPatcher {
    /**
     * Constructor
     *
     * @param {Object} options - Configuration options
     * @param {boolean} options.debug - Enable debug logging
     */
    constructor(options = {}) {
        this.debug = options.debug || false;

        // Label cache: attrName|value -> formatted text
        this.labelCache = new Map();

        // Term labels from WooCommerce (proper display names)
        // Structure: { 'pa_colors': { 'slug': 'Display Name', ... }, ... }
        this.termLabels = options.termLabels || {};

        // Cache size limit to prevent unbounded growth
        this.maxCacheSize = options.maxCacheSize || 500;

        // Statistics
        this.stats = {
            patchCalls: 0,
            optionsAdded: 0,
            optionsRemoved: 0,
            cacheHits: 0,
            cacheMisses: 0,
            termLabelHits: 0
        };
    }

    /**
     * Set term labels for proper display names
     *
     * @param {Object} labels - Map of taxonomy => {slug => label}
     */
    setTermLabels(labels) {
        this.termLabels = labels || {};
        this.log('Term labels set:', Object.keys(this.termLabels));
    }

    /**
     * Smart patch dropdown options - only add/remove changed options
     *
     * Instead of clearing and rebuilding the entire dropdown (which causes flickering),
     * this method:
     * 1. Calculates the diff between current and new options
     * 2. Adds only missing options at correct positions
     * 3. Removes only obsolete options
     * 4. Preserves current selection if still valid
     *
     * @param {HTMLSelectElement} select - The dropdown element
     * @param {Array} newOptions - Array of new option objects {value, label} or strings
     * @param {Object} options - Patch options
     * @param {boolean} options.allowRemoval - Whether to remove obsolete options (default: true)
     * @param {boolean} options.preserveValue - Whether to preserve current value (default: true)
     * @param {string} options.attrName - Attribute name for label formatting
     * @returns {boolean} Whether any changes were made
     */
    patchDropdownOptions(select, newOptions, options = {}) {
        const {
            allowRemoval = true,
            preserveValue = true,
            attrName = ''
        } = options;

        this.stats.patchCalls++;
        this.log('Patching dropdown:', attrName || select.name);

        // Store current value for restoration
        const currentValue = select.value;

        // Get existing options (excluding empty placeholder)
        const existingValues = new Set(
            Array.from(select.options)
                .filter(opt => opt.value !== '')
                .map(opt => opt.value)
        );

        // Normalize new options to array of {value, label}
        const normalizedNew = this.normalizeOptions(newOptions);
        const newValues = new Set(normalizedNew.map(opt => opt.value));

        // Calculate diff
        const toAdd = normalizedNew.filter(opt => !existingValues.has(opt.value));
        const toRemove = allowRemoval
            ? [...existingValues].filter(v => !newValues.has(v))
            : [];

        this.log(`Diff: +${toAdd.length} -${toRemove.length}`);

        let changed = false;

        // Set DOM updating flag for coordination
        if (typeof window.setHVPDOMUpdating === 'function') {
            window.setHVPDOMUpdating(true);
        }

        try {
            // Remove obsolete options first
            if (toRemove.length > 0) {
                toRemove.forEach(value => {
                    const option = Array.from(select.options).find(opt => opt.value === value);
                    if (option) {
                        option.remove();
                        this.stats.optionsRemoved++;
                        changed = true;
                    }
                });
                this.log('Removed options:', toRemove);
            }

            // Add missing options at correct positions
            if (toAdd.length > 0) {
                // Get sorted list of all values for positioning
                const sortedAllValues = normalizedNew.map(opt => opt.value);

                toAdd.forEach(opt => {
                    const option = document.createElement('option');
                    option.value = opt.value;
                    option.textContent = this.formatDisplayText(opt.value, opt.label, attrName);

                    // Find correct insertion point
                    const insertPoint = this.findInsertionPoint(select, opt.value, sortedAllValues);
                    if (insertPoint) {
                        select.insertBefore(option, insertPoint);
                    } else {
                        select.appendChild(option);
                    }
                    this.stats.optionsAdded++;
                    changed = true;
                });
                this.log('Added options:', toAdd.map(o => o.value));
            }

            // Restore selection if valid
            if (preserveValue && currentValue) {
                const isStillValid = newValues.has(currentValue);
                if (isStillValid) {
                    select.value = currentValue;
                } else {
                    this.log('Previous value no longer valid:', currentValue);
                }
            }

        } finally {
            // Clear DOM updating flag
            if (typeof window.setHVPDOMUpdating === 'function') {
                window.setHVPDOMUpdating(false);
            }
        }

        return changed;
    }

    /**
     * Find correct insertion point to maintain option order
     *
     * Given a sorted list of all values, finds the next existing option
     * after the target position to insert before.
     *
     * @param {HTMLSelectElement} select - The dropdown element
     * @param {string} value - The value to insert
     * @param {Array<string>} sortedValues - All values in desired order
     * @returns {HTMLOptionElement|null} Element to insert before, or null for append
     */
    findInsertionPoint(select, value, sortedValues) {
        const targetIndex = sortedValues.indexOf(value);

        if (targetIndex === -1) {
            return null; // Not in list, append at end
        }

        // Find next option that exists in DOM after this position
        for (let i = targetIndex + 1; i < sortedValues.length; i++) {
            const nextValue = sortedValues[i];
            const nextOption = Array.from(select.options)
                .find(opt => opt.value === nextValue);

            if (nextOption) {
                return nextOption; // Insert before this
            }
        }

        return null; // Append at end
    }

    /**
     * Normalize options to array of {value, label} objects
     *
     * Accepts various input formats:
     * - Array of strings: ['red', 'blue'] -> [{value: 'red', label: 'red'}, ...]
     * - Array of objects with value/label: [{value: 'red', label: 'Red'}, ...]
     * - Array of objects with value/slug: [{value: 'red', slug: 'red'}, ...]
     *
     * @param {Array} options - Options in any supported format
     * @returns {Array<{value: string, label: string}>} Normalized options
     */
    normalizeOptions(options) {
        if (!Array.isArray(options)) {
            return [];
        }

        return options.map(opt => {
            if (typeof opt === 'string') {
                return { value: opt, label: opt };
            }
            if (typeof opt === 'object' && opt !== null) {
                return {
                    value: opt.value || opt.slug || '',
                    label: opt.label || opt.value || opt.slug || ''
                };
            }
            return { value: String(opt), label: String(opt) };
        }).filter(opt => opt.value !== '');
    }

    /**
     * Clear the label cache
     */
    clearCache() {
        this.labelCache.clear();
        this.log('Label cache cleared');
    }

    /**
     * Get statistics
     *
     * @returns {Object} Stats object
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.labelCache.size,
            cacheHitRate: this.stats.cacheHits + this.stats.cacheMisses > 0
                ? (this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses) * 100).toFixed(1) + '%'
                : '0%'
        };
    }

    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = {
            patchCalls: 0,
            optionsAdded: 0,
            optionsRemoved: 0,
            cacheHits: 0,
            cacheMisses: 0
        };
    }

    /**
     * Log message (debug mode only)
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP DomPatcher]',
                'background: #10b981; color: #fff; padding: 2px 6px; border-radius: 3px;',
                ...args
            );
        }
    }
}

// Label formatting lives in a sibling module (350-line rule).
applyMixin(HVPDomPatcher.prototype, DomPatcherLabelMethods);

export default HVPDomPatcher;
